<template>
  <v-card
    class="meal-card standard-card-override"
    :class="{ 'meal-can-reserve': item.can_reserve }"
  >
    <v-overlay :value="loading.deleteMeal" absolute>
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
      <p class="mt-3 text-body-2 white--text">{{ $t("deleting_meal") }}</p>
    </v-overlay>

    <!-- Image Area with Actions -->
    <div class="image-area">
      <v-img
        v-if="hasValidImage"
        :src="mealImageUrl"
        height="220px"
        class="meal-image"
        gradient="to bottom, rgba(38,50,56,.15), rgba(38,50,56,.55)"
      >
        <!-- Title Overlay -->
        <div class="image-title-overlay">
          <div class="image-title">
            {{ item.name || $t("no_name") }}
          </div>
        </div>
      </v-img>
      <div v-else class="no-image-placeholder">
        <v-icon color="blue-grey lighten-2" size="64" class="meal-image-placeholder-icon">
          mdi-food-variant
        </v-icon>
        <div class="placeholder-title">
          {{ item.name || $t("no_name") }}
        </div>
      </div>

      <!-- Edit and Delete buttons in top-right -->
      <div class="action-btn-group">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              @click="$emit('edit', item)"
              color="white"
              class="action-btn"
              :disabled="loading.deleteMeal"
            >
              <v-icon small color="green darken-1">mdi-pencil-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("edit_meal") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              @click="confirmAndDeleteMeal"
              color="white"
              class="action-btn"
              :loading="loading.deleteMeal"
              :disabled="loading.deleteMeal"
            >
              <v-icon small color="red darken-1">mdi-delete-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("delete_meal") }}</span>
        </v-tooltip>
      </div>

      <!-- Floating image edit button -->
      <div class="floating-image-btn right">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              fab
              small
              color="white"
              class="elevation-3"
              v-bind="attrs"
              v-on="on"
              @click="openImageGallery"
              :disabled="loading.deleteMeal"
            >
              <v-icon color="blue darken-1">mdi-image-edit-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("edit_image") }}</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Card Content -->
    <v-card-text class="card-content">
      <!-- Menu Type Groups with full info -->
      <div class="meal-groups mb-3">
        <v-row justify="space-between" align="end" no-gutters>
          <div>
            <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium">
              {{ $t("menu_type") }}
            </div>
            <v-chip
              v-for="group in item.groups"
              :key="group.id"
              small
              label
              color="primary"
              class="mr-1 mb-1"
            >
              {{ $t(group.menu_type?.name) || group.menu_type?.name }}
            </v-chip>
          </div>
          <div class="font-weight-medium">ID: {{ item.id }}</div>
        </v-row>
      </div>

      <!-- Active Shifts -->
      <div v-if="activeShiftsCount > 0" class="meal-shifts mb-3">
        <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium">
          {{ $t("shifts") }}
        </div>
        <v-chip
          v-for="shift in activeShifts"
          :key="shift.number"
          small
          label
          color="success"
          class="mr-1 mb-1"
        >
          <v-icon left x-small>mdi-clock-outline</v-icon>
          {{ shift.label }}
        </v-chip>
      </div>

      <!-- Price and Order Info -->
      <div class="meal-info mb-3">
        <v-row dense align="center">
          <v-col cols="6">
            <text-field
              v-model="item.poredak"
              :label="$t('order_position')"
              type="number"
              hide-details
              @input="$emit('order-changed', item)"
              @click.native.stop
              :disabled="loading.deleteMeal"
            />
          </v-col>
          <v-col v-if="item.price" cols="6" class="text-right">
            <div class="text-caption grey--text text--darken-1">{{ $t("price") }}</div>
            <div class="text-body-1 font-weight-bold primary--text">
              {{ item.price }}{{ restaurant.settings?.price || "" }}
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Foods List -->
      <div v-if="(item.foods || []).length" class="meal-foods mb-3">
        <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium">
          {{ $t("meals_main") }} ({{ item.foods.length }})
        </div>
        <div class="foods-list">
          <div v-for="(food, ind) in item.foods" :key="ind" class="food-item">
            <v-icon x-small color="grey" class="mr-1">mdi-circle-small</v-icon>
            <span class="text-body-2">{{ toLatin(food.name) }}</span>
          </div>
        </div>
      </div>

      <!-- Labels -->
      <div v-if="(item.labels || []).length" class="meal-labels mb-3">
        <div class="text-caption grey--text text--darken-1 mb-2 font-weight-medium">
          {{ $t("labels") }}
        </div>
        <v-chip
          v-for="label in item.labels"
          :key="label.id"
          x-small
          outlined
          color="primary"
          class="mr-1 mb-1"
        >
          {{ label.name }}
        </v-chip>
      </div>

      <!-- Reserve checkbox -->
      <v-checkbox
        v-if="restaurant.settings?.can_reserve && isReservable"
        :input-value="!!item.can_reserve"
        :label="$t('reserve_meal')"
        hide-details
        dense
        color="primary"
        class="mt-2"
        @change="handleReserveChange"
        :disabled="loading.deleteMeal"
      ></v-checkbox>
    </v-card-text>

    <!-- Dialogs -->
    <food-menu-gallery-dialog
      v-model="isGalleryDialogOpen"
      :meal="item"
    ></food-menu-gallery-dialog>

    <global-confirm-box ref="confirmBox"></global-confirm-box>
  </v-card>
</template>

<script>
import FoodMenuGalleryDialog from "@/components/foodMenu/dialogs/FoodMenuGalleryDialog.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import menuService from "@/services/menu";

export default {
  name: "MealCard",
  mixins: [hasSnackbar],
  components: {
    FoodMenuGalleryDialog,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    images: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      mealImageUrl: "./media/img/no-image.webp",
      isGalleryDialogOpen: false,
      loading: {
        deleteMeal: false,
      },
    };
  },
  methods: {
    handleReserveChange(value) {
      const newValue = value ? this.item.id : null;
      // Update local item value to trigger CSS class change
      this.item.can_reserve = newValue;
      this.$emit("reserve-changed", newValue, this.item);
    },
    openImageGallery() {
      if (this.loading.deleteMeal) return;
      this.isGalleryDialogOpen = true;
    },
    confirmAndDeleteMeal() {
      if (this.loading.deleteMeal) return;

      this.$refs.confirmBox
        .open(this.$t("are_you_sure"), this.$t("delete_meal_confirm"), {
          color: "error",
        })
        .then(() => {
          this.loading.deleteMeal = true;
          menuService
            .deleteCloneMeal(this.item)
            .then(({ data }) => {
              if (data === "OK") {
                this.showSuccessSnackbar(this.$t("meal_deleted") || "Jelo obrisano");
                this.$emit("meal-deleted", this.item.id);
              }
            })
            .catch((error) => {
              console.error("Error deleting meal:", error);
              if (error?.response?.data?.status === "failed") {
                this.showErrorSnackbar(
                  error.response.data.message || this.$t("error_deleting_meal")
                );
              } else {
                this.showErrorSnackbar(
                  this.$t("error_deleting_meal") || "Greška pri brisanju jela"
                );
              }
            })
            .finally(() => {
              this.loading.deleteMeal = false;
            });
        })
        .catch((err) => console.warn(err));
    },
    loadMealImage() {
      if (!this.item) {
        this.mealImageUrl = "./media/img/no-image.webp";
        return;
      }
      const imageFromGallery = (this.images || []).find(
        (img) => img.id === this.item.image_id
      );
      const storageUrl = this.item.image?.host || imageFromGallery?.host;
      const imageName = this.item.image?.name || imageFromGallery?.name;

      // Handle cloned meal images
      if (this.item.clone_id != null && imageName && storageUrl) {
        this.mealImageUrl = `${storageUrl}/gallery/${this.item.clone_id}/medium/${imageName}`;
        return;
      }

      // Handle regular restaurant meal images
      const imageDirectory = `${storageUrl}/gallery/${this.restaurant.id}/medium/`;
      if (imageName && storageUrl) {
        this.mealImageUrl = imageDirectory + imageName;
      } else {
        this.mealImageUrl = "./media/img/no-image.webp";
      }
    },
  },
  computed: {
    restaurant() {
      return this.$store.state.restaurant;
    },
    hasValidImage() {
      return this.mealImageUrl && this.mealImageUrl !== "./media/img/no-image.webp";
    },
    isReservable() {
      return this.item.groups.some((group) => group.group !== "group");
    },
    activeShifts() {
      const shifts = [];
      if (this.item.shift_one) {
        shifts.push({ number: 1, label: this.$t("1st_shift") });
      }
      if (this.item.shift_two) {
        shifts.push({ number: 2, label: this.$t("2nd_shift") });
      }
      if (this.item.shift_three) {
        shifts.push({ number: 3, label: this.$t("3rd_shift") });
      }
      if (this.item.shift_four) {
        shifts.push({ number: 4, label: this.$t("4th_shift") });
      }
      return shifts;
    },
    activeShiftsCount() {
      return this.activeShifts.length;
    },
    shiftsBadge() {
      if (this.item.can_reserve) return "";
      return this.activeShiftsCount > 0 ? `${this.activeShiftsCount}` : "";
    },
    mealSubtitle() {
      const parts = [];

      // Number of groceries
      if (this.item.foods && this.item.foods.length > 0) {
        parts.push(`${this.item.foods.length} ${this.$t("ingredients").toLowerCase()}`);
      }

      // Number of shifts
      if (this.activeShiftsCount > 0) {
        parts.push(`${this.activeShiftsCount} ${this.$t("shifts").toLowerCase()}`);
      }

      return parts.join(" • ");
    },
  },
  watch: {
    item: {
      handler() {
        this.loadMealImage();
      },
      immediate: true,
      deep: true,
    },
    images: {
      handler() {
        this.loadMealImage();
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_variables.scss";

// Card styling
.meal-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s;
  overflow: visible; // Allow floating button to overlap
}

.meal-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

// Reserve indicator
.meal-can-reserve {
  border-left: 4px solid #ffc107;
}

// Image area
.image-area {
  position: relative;
  height: 220px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  background: linear-gradient(135deg, #eceff1 60%, #b0bec5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.meal-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

// No image placeholder
.no-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
}

.meal-image-placeholder-icon {
  margin-bottom: 16px;
}

.placeholder-title {
  color: #546e7a;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 8px;
}

.placeholder-subtitle {
  color: #78909c;
  font-size: 0.875rem;
}

// Image title overlay
.image-title-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
}

.image-title {
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.3;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.image-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  margin-top: 4px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

// Action buttons in top-right
.action-btn-group {
  position: absolute;
  top: 10px;
  right: 12px;
  display: flex;
  flex-direction: row;
  gap: 6px;
  z-index: 2;
}

.action-btn {
  background: rgba(255, 255, 255, 0.92) !important;
  box-shadow: 0 2px 8px rgba(38, 50, 56, 0.08);
  border-radius: 50%;
  transition: background 0.2s, box-shadow 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 1) !important;
    box-shadow: 0 4px 16px rgba(38, 50, 56, 0.16);
  }
}

// Floating image edit button
.floating-image-btn {
  position: absolute;
  z-index: 5;
  pointer-events: auto;

  .v-btn {
    border: 2px solid #e3eaf3;
    background: #fff !important;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 4px 16px rgba(33, 150, 243, 0.18);
    }
  }
}

.floating-image-btn.right {
  top: 200px;
  right: 18px;
}

// Card content
.card-content {
  padding: 16px 24px;
}

// Meal sections styling
.meal-groups,
.meal-shifts,
.meal-info,
.meal-foods,
.meal-labels {
  padding: 0;
}

// Foods list compact display
.foods-list {
  max-height: 200px;
  overflow-y: auto;

  .food-item {
    display: flex;
    align-items: center;
    padding: 4px 0;

    &:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }
}

// Scrollbar styling for foods list
.foods-list::-webkit-scrollbar {
  width: 4px;
}

.foods-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 2px;
}

.foods-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
}
</style>
